import React, { useState, useEffect } from 'react';
import { X, Package } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import { handleQuoteRequest, mockData } from '../mock';

const QuoteModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    productType: '',
    quantity: '',
    deliveryLocation: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const result = await handleQuoteRequest(formData);
      if (result.success) {
        toast.success(result.message);
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          productType: '',
          quantity: '',
          deliveryLocation: '',
          message: ''
        });
        onClose();
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      data-testid="quote-modal-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <div data-testid="quote-modal" className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-emerald-700 to-emerald-900 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-white/20 backdrop-blur-md rounded-lg flex items-center justify-center">
              <Package size={24} />
            </div>
            <div>
              <h2 className="text-2xl font-bold">Request a Quote</h2>
              <p className="text-emerald-100 text-sm">Get a customized quote for your needs</p>
            </div>
          </div>
          <button
            data-testid="quote-modal-close-btn"
            onClick={onClose}
            className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="quote-name" className="block text-sm font-semibold text-gray-700 mb-2">
                Full Name *
              </label>
              <Input
                id="quote-name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Enter your name"
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label htmlFor="quote-email" className="block text-sm font-semibold text-gray-700 mb-2">
                Email Address *
              </label>
              <Input
                id="quote-email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="your.email@example.com"
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="quote-phone" className="block text-sm font-semibold text-gray-700 mb-2">
                Phone Number *
              </label>
              <Input
                id="quote-phone"
                name="phone"
                type="tel"
                required
                value={formData.phone}
                onChange={handleChange}
                placeholder="+91 98765 43210"
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>

            <div>
              <label htmlFor="quote-company" className="block text-sm font-semibold text-gray-700 mb-2">
                Company / Business Name
              </label>
              <Input
                id="quote-company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="Optional"
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="quote-product" className="block text-sm font-semibold text-gray-700 mb-2">
                Product Type *
              </label>
              <select
                data-testid="quote-product-select"
                id="quote-product"
                name="productType"
                required
                value={formData.productType}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:border-emerald-500 focus:ring-emerald-500"
              >
                <option value="">Select product</option>
                {mockData.products.map((product) => (
                  <option key={product.id} value={product.name}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="quote-quantity" className="block text-sm font-semibold text-gray-700 mb-2">
                Quantity Required *
              </label>
              <Input
                id="quote-quantity"
                name="quantity"
                type="text"
                required
                value={formData.quantity}
                onChange={handleChange}
                placeholder="e.g., 500 eggs per day"
                className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
              />
            </div>
          </div>

          <div>
            <label htmlFor="quote-location" className="block text-sm font-semibold text-gray-700 mb-2">
              Delivery Location *
            </label>
            <Input
              id="quote-location"
              name="deliveryLocation"
              type="text"
              required
              value={formData.deliveryLocation}
              onChange={handleChange}
              placeholder="City, State"
              className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
            />
          </div>

          <div>
            <label htmlFor="quote-message" className="block text-sm font-semibold text-gray-700 mb-2">
              Additional Requirements
            </label>
            <Textarea
              id="quote-message"
              name="message"
              rows={4}
              value={formData.message}
              onChange={handleChange}
              placeholder="Tell us about your specific requirements, delivery schedule, or any other details..."
              className="border-gray-300 focus:border-emerald-500 focus:ring-emerald-500"
            />
          </div>

          <div className="flex gap-4">
            <Button
              data-testid="quote-modal-cancel-btn"
              type="button"
              onClick={onClose}
              variant="outline"
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              data-testid="quote-modal-submit-btn"
              type="submit"
              disabled={isSubmitting}
              className="flex-1 bg-emerald-700 hover:bg-emerald-800 text-white"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuoteModal;
